import { SVGIcon } from '@progress/kendo-svg-icons';
import React, { createElement, useCallback, useEffect, useState } from 'react';
import { useMultiWindowContext } from '../../../app/components/multi-window/context/multi-window-context';
import { useEvent } from '../../../app/contexts/event-bus';
import { useFlightWorker } from '../../../app/hooks/use-flight-worker';
import { AirplaneOutline, AirportTowerOutline, FlightOutline, WeatherRadarOutline } from '../../../icons/icons';
import { MessageType } from '../../../services/actor-messages';
import { ActiveFlightContextMenuData, MapContextMenuData, MapContextMenuInfo } from '../../../services/map-interaction-service';
import { Menu } from '../../common/menu/DropdownMenu';
import { DrawingObjectContextMenu } from '../../drawing-object/DrawingObjectContextMenu';
import { ActiveFlightContextMenu } from './ActiveFlightContextMenu';
import { AirportContextMenu } from './AirportContextMenu';
import { FlightContextMenuContent } from './FlightContextMenuContent';
import { MapContextMenu } from './MapContextMenu';
import { NavDataContextMenu } from './NavDataContextMenu';
import { PreDepartureContextMenu } from './PreDepartureContextMenu';
import { RouteAnalyzerContextMenu } from './RouteAnalyzerContextMenu';
import { WeatherContextMenu } from './WeatherContextMenu';

export function ContextMenus() {
    const [data, setData] = useState<Exclude<MapContextMenuInfo, { elements: [] }> | null>(null);
    const multiWindowContext = useMultiWindowContext();
    const thisWindow = multiWindowContext.active ? multiWindowContext.window : self;

    useEvent(
        'context-menu',
        useCallback(
            (e) => {
                // In multiple windows scenario, this hook is used in a portal and in main window
                // Keep track of the source of the event
                if (e.view && e?.view !== thisWindow) {
                    // Ignore events originating from a different window
                    return;
                }
                if (e.elements.length === 0) {
                    setData(null);
                } else {
                    setData(e as typeof data);
                }
            },
            [thisWindow]
        )
    );

    if (!data) {
        return null;
    }

    const elements = data.elements.map((menu) => <ElementToComponent key={menu.kind} menu={menu} />);
    const shouldDisplayFlat = data.elements.length === 1 && !data.elements.some((x) => x.kind === 'flight' && x.flightIds.length > 1);

    return (
        <MapContextMenu open position={{ x: data.event.clientX, y: data.event.clientY }}>
            {shouldDisplayFlat ? elements : <Menu>{elements}</Menu>}
        </MapContextMenu>
    );
}

type ContextMenuComponents = {
    [K in MapContextMenuData['kind']]: React.FC<{ info: Extract<MapContextMenuData, { kind: K }> }>;
};

function ElementToComponent({ menu: element }: { menu: MapContextMenuData }) {
    if (element.kind === 'flight') {
        return <FlightsElements data={element} />;
    }

    return (
        <Menu icon={icons[element.kind]} label={getName(element)}>
            {createElement(contextMenuComponents[element.kind] as React.FC<{ info: MapContextMenuData }>, { info: element })}
        </Menu>
    );
}

function FlightsElements({ data }: { data: ActiveFlightContextMenuData }) {
    const [acids, setAcids] = React.useState<{ id: number; acid: string }[]>([]);
    const worker = useFlightWorker();
    useEffect(() => {
        setAcids([]);
        worker.actor.sendAsync({ type: MessageType.getFlightAcids, data: data.flightIds }).then(setAcids);
    }, [worker, data]);

    return acids.map((x) => (
        <Menu label={x.acid} key={x.id} icon={AirplaneOutline}>
            <FlightContextMenuContent id={x.id} />
        </Menu>
    ));
}

const names: Record<MapContextMenuData['kind'], string> = {
    flight: 'Flight',
    airport: 'Airport',
    weather: 'Weather',
    navdata: 'Nav layers',
    'route-analyzer': 'Route analyzer',
    'drawing-object': 'Drawing objects',
    'pre-dep-flight': 'Pre-departure flights'
};

function getName(data: MapContextMenuData) {
    if (data.kind === 'airport') {
        return data.airportId;
    }

    return names[data.kind];
}

const icons: Record<MapContextMenuData['kind'], SVGIcon> = {
    flight: FlightOutline,
    airport: AirportTowerOutline,
    weather: WeatherRadarOutline,
    navdata: WeatherRadarOutline,
    'route-analyzer': WeatherRadarOutline,
    'drawing-object': WeatherRadarOutline,
    'pre-dep-flight': WeatherRadarOutline
};

const contextMenuComponents: ContextMenuComponents = {
    weather: WeatherContextMenu,
    airport: AirportContextMenu,
    flight: ActiveFlightContextMenu,
    'pre-dep-flight': PreDepartureContextMenu,
    navdata: NavDataContextMenu,
    'route-analyzer': RouteAnalyzerContextMenu,
    'drawing-object': DrawingObjectContextMenu
};
